import './Overview.scss'

import {CardSwitch, CardSwitchType} from './cards/CardSwitch'
import {Carousel, useCarousel} from '@codeurs/carousel'
import {LinkType, MainUrlTypes} from '../../types'
import React, {Fragment, useState} from 'react'

import {CardLayoutType} from '../ui/Card'
import {MarkedTitle} from '../ui/MarkedTitle'
import {Theme} from '../Theme'
import {style} from '../../util/style'
import {useHistory} from 'react-router'
import {useTranslation} from '../../util/i18n'

const styles = style('overview', ['items', 'pagination'])

export type OverviewType = OverviewItemsType & {
	container?: boolean
	className?: string
	title?: string
}

type OverviewItemsType = {
	layout?: CardLayoutType
	items: Array<CardSwitchType>
	card_linklabel?: string
	carousel?: boolean
	carouselFrom?: number
	maxBeforeLoadMore?: number
	loadItemsPerLoadMore?: number
	disableLoadmoreByLink?: LinkType | MainUrlTypes
}

export const Overview: React.FC<OverviewType> = ({
	container,
	className,
	title,
	layout,
	items,
	card_linklabel,
	carousel,
	carouselFrom = 3,
	...props
}) => {
	if (!items || items.length === 0) return null
	return (
		<OverviewWrapper
			container={container}
			className={className}
			title={title}
			layout={layout}
			items={items}
			carousel={carousel && carouselFrom <= items.length}
		>
			<OverviewItems
				layout={layout}
				items={items}
				card_linklabel={card_linklabel}
				carousel={carousel && carouselFrom <= items.length}
				{...props}
			/>
		</OverviewWrapper>
	)
}

const OverviewWrapper: React.FC<OverviewType> = ({
	container = true,
	className,
	layout,
	title,
	items,
	carousel = false,
	children
}) => {
	const ContainerTag = container ? Theme.Container : Fragment

	return (
		<div
			className={styles()
				.with(className)
				.mod(layout)
				.mod({columns2: !layout && items?.length <= 2})
				.mod({carousel})
				.mod({title})}
		>
			<ContainerTag>
				<MarkedTitle
					className={styles('markedtitle')}
					title={title}
					direction={'left'}
					as="h2"
				/>
				{children}
			</ContainerTag>
		</div>
	)
}

const OverviewItems: React.FC<OverviewItemsType> = ({
	items,
	card_linklabel,
	carousel = false,
	maxBeforeLoadMore = 6,
	loadItemsPerLoadMore = 3,
	disableLoadmoreByLink,
	layout
}) => {
	const t = useTranslation('overview')
	const [maxItems, setMaxItems] = useState(maxBeforeLoadMore)
	const itemsLength = items?.length
	const Tag: any = carousel ? Carousel : Fragment
	const attrsTag = carousel ? {full: true, ...useCarousel()} : {}
	const history = useHistory()
	const hasMoreItems = itemsLength > maxItems
	return (
		<div
			className={styles
				.items()
				.mod(layout)
				.mod({columns2: !layout && items?.length <= 2})
				.mod({carousel})}
		>
			<div className={styles.items('items')}>
				<Tag {...attrsTag}>
					{items.slice(0, maxItems).map((item, i) => {
						if (!item || (!item.image && !item.title && !item.text)) return null
						const cardSwitchLayout = getCardSwitchLayoutByChannel(item?.channel)
						return (
							<div
								key={i}
								className={styles.items('items-item').mod({carousel})}
							>
								<CardSwitch
									layout={cardSwitchLayout}
									card={item}
									card_linklabel={card_linklabel}
								/>
							</div>
						)
					})}
				</Tag>
			</div>
			{hasMoreItems && (
				<div className={styles.items('loadmore')}>
					<Theme.Button
						onClick={() => {
							disableLoadmoreByLink
								? history.push(disableLoadmoreByLink?.url)
								: setMaxItems(maxItems + loadItemsPerLoadMore)
						}}
					>
						{t.load_more}
					</Theme.Button>
				</div>
			)}
		</div>
	)
}

const getCardSwitchLayoutByChannel = (channel: string): CardLayoutType => {
	switch (channel) {
		case 'product_detail_physical':
		case 'product_detail_downloadable':
			return 'product'
		case 'agenda_detail':
		case 'event':
		case 'edition':
			return 'event'
		case 'project_detail':
			return 'project'
		default:
			return 'page'
	}
}
